import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Loader2, Plus, Pencil, Trash2, ShieldCheck, Eye, EyeOff, FileText } from "lucide-react";
import { formatDate } from "@/lib/qa";
import { toast } from "sonner";

export default function AdminArticles() {
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("all");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = () => {
        setLoading(true);
        const params = {};
        if (status !== "all") params.status = status;
        API.get("/admin/articles", { params }).then(({ data }) => setItems(data))
            .catch(e => setError(apiError(e))).finally(() => setLoading(false));
    };
    useEffect(load, [status]);

    const togglePublish = async (a) => {
        try {
            const url = a.status === "published" ? `/admin/articles/${a.id}/unpublish` : `/admin/articles/${a.id}/publish`;
            await API.post(url);
            toast.success(a.status === "published" ? "Articolo depubblicato" : "Articolo pubblicato");
            load();
        } catch (e) { toast.error(apiError(e)); }
    };
    const remove = async (id) => {
        try { await API.delete(`/admin/articles/${id}`); toast.success("Articolo eliminato"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2 text-muted-foreground">
                <Link to="/admin"><ArrowLeft className="size-4 mr-1" /> Panoramica admin</Link>
            </Button>
            <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                <ShieldCheck className="size-3.5" /> Pannello amministratore
            </div>
            <div className="mt-1 flex items-center justify-between">
                <h1 className="font-serif text-3xl font-semibold" data-testid="admin-articles-title">Articoli</h1>
                <Button asChild data-testid="admin-articles-new"><Link to="/admin/articoli/nuovo"><Plus className="size-4 mr-2" /> Nuovo articolo</Link></Button>
            </div>

            <div className="mt-6 max-w-xs">
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="admin-articles-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tutti gli articoli</SelectItem>
                        <SelectItem value="draft">Bozze</SelectItem>
                        <SelectItem value="published">Pubblicati</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {error && <Alert variant="destructive" className="mt-4"><AlertDescription>{error}</AlertDescription></Alert>}

            <div className="mt-6">
                {loading ? <div className="text-center py-16"><Loader2 className="size-6 mx-auto animate-spin text-muted-foreground" /></div>
                    : items.length === 0 ? (
                        <Card className="border-dashed border-2 bg-muted/30"><CardContent className="py-14 text-center">
                            <FileText className="size-10 mx-auto text-muted-foreground/40" />
                            <p className="mt-3 text-muted-foreground">Nessun articolo ancora. Iniziamo?</p>
                            <Button asChild className="mt-5"><Link to="/admin/articoli/nuovo">Crea il primo articolo</Link></Button>
                        </CardContent></Card>
                    ) : (
                        <div className="space-y-3" data-testid="admin-articles-list">
                            {items.map(a => (
                                <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-5 flex items-start gap-4" data-testid={`admin-article-${a.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            {a.category && <span className="text-xs font-semibold text-primary uppercase tracking-widest">{a.category.name}</span>}
                                            <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${a.status === "published" ? "bg-emerald-600 text-white border-emerald-600" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                                                {a.status === "published" ? "Pubblicato" : "Bozza"}
                                            </span>
                                        </div>
                                        <div className="font-semibold text-slate-900">{a.title}</div>
                                        <div className="text-xs text-muted-foreground mt-1">Modificato il {formatDate(a.updated_at)}</div>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button size="sm" variant="ghost" onClick={() => togglePublish(a)} data-testid={`art-toggle-${a.id}`}>
                                            {a.status === "published" ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                                        </Button>
                                        <Button asChild size="sm" variant="ghost" data-testid={`art-edit-${a.id}`}><Link to={`/admin/articoli/${a.id}`}><Pencil className="size-4" /></Link></Button>
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" data-testid={`art-delete-${a.id}`}><Trash2 className="size-4" /></Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>Eliminare "{a.title}"?</AlertDialogTitle>
                                                    <AlertDialogDescription>Operazione irreversibile.</AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                                                    <AlertDialogAction onClick={() => remove(a.id)} className="bg-destructive text-destructive-foreground">Elimina</AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
            </div>
        </div>
    );
}
